/**
 * /validation/my-tests — the tester's view.
 *
 * A tester is any Entra user. They see exactly the tests assigned to them —
 * never the run, the plan, or anyone else's tests — and record step results,
 * attach evidence, and give a verdict. The server stamps actor and UTC time.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconChecklist } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  addMyEvidence,
  downloadEvidence,
  listMyTests,
  startMyTest,
  submitMyResult,
  type EvidenceOut,
  type MyTestOut,
} from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { ManualResultForm } from "./ManualResultForm";
import { TEST_STATUS_COLOR, TEST_STATUS_LABEL, riskColor } from "./stageMeta";

export function MyTests() {
  const getToken = useAccessToken();
  const [items, setItems] = useState<MyTestOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      setItems((await listMyTests(token)).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (updated: MyTestOut) =>
    setItems((prev) =>
      (prev ?? []).map((i) => (i.test.test_id === updated.test.test_id ? updated : i)),
    );

  const open = (items ?? []).filter(
    (i) => !["passed", "failed", "blocked"].includes(i.test.status),
  );
  const done = (items ?? []).filter((i) => ["passed", "failed", "blocked"].includes(i.test.status));

  const onDownload = async (e: EvidenceOut) => {
    try {
      const token = await getToken();
      const blob = await downloadEvidence(token, e.evidence_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = e.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError("Could not download evidence", err);
    }
  };

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>My tests</Title>
        <Text c="dimmed" size="sm">
          Tests assigned to you. Work through the steps, attach evidence, record the result.
        </Text>
      </div>

      {error && (
        <Alert variant="light" color="red" title="Could not load your tests">
          {error}
        </Alert>
      )}

      {items === null ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" size={48} radius="xl">
              <IconChecklist size={26} />
            </ThemeIcon>
            <Text fw={600}>Nothing assigned to you</Text>
            <Text c="dimmed" size="sm">
              When a validation lead assigns you a manual test, it appears here.
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          {open.length > 0 && (
            <TestGroup
              title={`To do (${open.length})`}
              items={open}
              getToken={getToken}
              onChange={replace}
              onDownload={onDownload}
            />
          )}
          {done.length > 0 && (
            <TestGroup
              title={`Completed (${done.length})`}
              items={done}
              getToken={getToken}
              onChange={replace}
              onDownload={onDownload}
            />
          )}
        </>
      )}
    </Stack>
  );
}

interface GroupProps {
  title: string;
  items: MyTestOut[];
  getToken: () => Promise<string>;
  onChange: (updated: MyTestOut) => void;
  onDownload: (e: EvidenceOut) => Promise<void>;
}

function TestGroup({ title, items, getToken, onChange, onDownload }: GroupProps) {
  return (
    <Paper withBorder radius="md" p="md">
      <Text fw={600} mb="sm">
        {title}
      </Text>
      <Accordion variant="separated">
        {items.map((item) => {
          const t = item.test;
          return (
            <Accordion.Item key={t.test_id} value={t.test_id}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Badge variant="outline" color="gray" size="sm">
                      {t.code}
                    </Badge>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" truncate>
                        {t.title}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {item.run_title} · {item.vault_product}
                        {item.change_ref ? ` · ${item.change_ref}` : ""} · {t.steps.length} steps
                      </Text>
                    </div>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    {t.gxp_risk && (
                      <Badge size="sm" variant="light" color={riskColor(t.gxp_risk)}>
                        {t.gxp_risk}
                      </Badge>
                    )}
                    <Badge size="sm" variant="light" color={TEST_STATUS_COLOR[t.status]}>
                      {TEST_STATUS_LABEL[t.status]}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {t.objective && (
                  <Text size="sm" c="dimmed" mb="sm">
                    {t.objective}
                  </Text>
                )}
                <ManualResultForm
                  test={t}
                  onStart={async () => {
                    try {
                      onChange(await startMyTest(await getToken(), t.test_id));
                    } catch (err) {
                      toastApiError("Could not start the test", err);
                    }
                  }}
                  onUpload={async (file, note) => {
                    try {
                      onChange(await addMyEvidence(await getToken(), t.test_id, file, note));
                      toastSuccess("Evidence attached", file.name);
                    } catch (err) {
                      toastApiError("Could not attach evidence", err);
                    }
                  }}
                  onSubmit={async (input) => {
                    try {
                      onChange(await submitMyResult(await getToken(), t.test_id, input));
                      toastSuccess("Result recorded", `${t.code} — ${input.verdict}`);
                    } catch (err) {
                      toastApiError("Could not record the result", err);
                    }
                  }}
                  onDownloadEvidence={(e) => void onDownload(e)}
                />
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Paper>
  );
}
